import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router";
import { AlertCircle, Loader2, Users } from "lucide-react";
import { SchoolLayout } from "../components/ui/school/SchoolLayout";
import ClassDetailHeader from "../components/ui/school/class-overview/class-detail-header";
import { ClassStatsCards } from "../components/ui/school/class-overview/class-stats-cards";
import { StudentsTable } from "../components/ui/school/class-overview/students-data";
import { SubjectsTabSection } from "../components/ui/school/class-overview/subjects-tab-section";
import { getClass, getClassStudents } from "../hooks/api/schoolApi";

const ClassOverviewPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [classData, setClassData] = useState(null);
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!id || id === "undefined") {
      setError("No class ID provided.");
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    Promise.all([getClass(id), getClassStudents(id)])
      .then(([cls, studs]) => {
        setClassData(cls);
        setStudents(
          (studs ?? []).map((s) => ({
            id: s._id,
            name: `${s.firstName ?? ""} ${s.lastName ?? ""}`.trim() || "Student",
            studentId: s.studentId ?? s._id?.slice(-6) ?? "—",
            grade: s.classLevel ?? s.preferences?.classLevel ?? "—",
            gpa: String(s.performanceMetrics?.academicScore ?? "—"),
            careerPath: s.careerInterests?.[0] ?? s.selectedBroadPaths?.[0]?.replace("broad_", "").replace(/_/g, " ") ?? "—",
            status: s.status ?? "active",
          }))
        );
      })
      .catch((err) => {
        setError(err?.response?.data?.error?.message ?? "Could not load class details.");
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <SchoolLayout>
        <div className="flex items-center justify-center py-20 gap-3 text-gray-400">
          <Loader2 className="w-5 h-5 animate-spin" />
          <span>Loading class…</span>
        </div>
      </SchoolLayout>
    );
  }

  if (error) {
    return (
      <SchoolLayout>
        <div className="p-8 max-w-lg">
          <div className="flex items-center gap-3 bg-red-50 border border-red-200 rounded-2xl p-5 text-red-700">
            <AlertCircle className="w-5 h-5 shrink-0" />
            <div>
              <p className="font-bold text-sm">{error}</p>
              <button onClick={() => navigate("/class-management")} className="text-xs underline mt-1">
                Back to Class Management
              </button>
            </div>
          </div>
        </div>
      </SchoolLayout>
    );
  }

  const subjects = classData?.subjects ?? [];

  return (
    <SchoolLayout>
      <main className="max-w-7xl mx-auto px-6 py-8">
        <ClassDetailHeader
          className={classData?.name ?? classData?.level ?? "Class"}
          classTeacher={classData?.classTeacherId ?? "—"}
          studentCount={students.length}
        />
        <ClassStatsCards
          classAverage={classData?.avgScore ?? 0}
          topPerformer={students[0]?.name ?? "—"}
          subjectsTaken={subjects.length}
        />

        {students.length === 0 ? (
          <div className="mt-8 text-center py-16 space-y-3">
            <Users className="w-10 h-10 text-slate-300 mx-auto" />
            <p className="font-bold text-slate-700">No students in this class yet</p>
            <p className="text-slate-400 text-sm">Add students from the Students Management page and assign them to this class.</p>
          </div>
        ) : (
          <StudentsTable students={students} />
        )}

        {subjects.length > 0 && (
          <SubjectsTabSection subjects={subjects.map((s, i) => ({
            id: i,
            name: typeof s === "string" ? s : s.name ?? s,
            teacher: "—",
            completion: 0,
            avgScore: 0,
            status: "active",
          }))} />
        )}
      </main>
    </SchoolLayout>
  );
};

export default ClassOverviewPage;
